import { z } from "zod";
import { AddressSchema } from "../schemas/common.schema";

// ADV-995 — Adva-canonical input types and the PartnerSlug enum.
//
// These types are produced by per-partner adapters (the anti-corruption
// boundary, see thoughts/shared/pm/prds/2026-05-14-inbound-webhook-gateway.md
// §5.4). Every input carries `partner_external_id` so the receiver pipeline
// can write to `integrations.external_ids` and reconcile identity across
// partners.

// ---------------------------------------------------------------------------
// PartnerSlug — one place to add new partners. Mirrors PRD §3 inventory.
// ---------------------------------------------------------------------------

export const PARTNER_SLUGS = [
  // Infrastructure (Adva-managed; never replaced)
  "stripe",
  "twilio",
  "postmark",
  "google_workspace",
  "microsoft_graph",

  // Wedge (integrate now, replace later via first-party features)
  "jobber",
  "servicetitan",
  "housecall_pro",
  "service_fusion",
  "gohighlevel",
  "hubspot",
  "quickbooks_online",
  "xero",
  "companycam",

  // Lead-gen (always integrate; never replaced)
  "callrail",
  "thumbtack",
  "angi_leads",
  "angi_ads",
  "google_lsa",

  // iPaaS catch-all (Zapier / Make / n8n / custom)
  "generic",
] as const;

export type PartnerSlug = (typeof PARTNER_SLUGS)[number];

export const PartnerSlugSchema = z.enum(PARTNER_SLUGS).describe("Partner Slug");

// ---------------------------------------------------------------------------
// AdvaContactInput — minimum needed to upsert into core.contacts
// ---------------------------------------------------------------------------

export const AdvaContactInputSchema = z.object({
  partner_external_id: z.string().describe("Partner External ID"),
  first_name: z.string().optional().describe("First Name"),
  last_name: z.string().optional().describe("Last Name"),
  email: z.string().optional().describe("Email"),
  phone: z.string().optional().describe("Phone"),
  company_name: z.string().optional().describe("Company Name"),
  address: AddressSchema.optional().describe("Address"),
});
export type AdvaContactInput = z.infer<typeof AdvaContactInputSchema>;

// ---------------------------------------------------------------------------
// AdvaJobInput — minimum needed to upsert into ops.jobs
// ---------------------------------------------------------------------------

export const AdvaJobInputSchema = z.object({
  partner_external_id: z.string().describe("Partner External ID"),
  customer_partner_external_id: z
    .string()
    .optional()
    .describe("Customer Partner External ID"),
  subject: z.string().optional().describe("Subject"),
  status: z.string().optional().describe("Status"),
  scheduled_at: z.string().datetime().optional().describe("Scheduled At"),
  completed_at: z.string().datetime().optional().describe("Completed At"),
  address: AddressSchema.optional().describe("Address"),
  notes: z.string().optional().describe("Notes"),
});
export type AdvaJobInput = z.infer<typeof AdvaJobInputSchema>;

// ---------------------------------------------------------------------------
// AdvaInvoiceInput — minimum needed to upsert into finance.invoices
// ---------------------------------------------------------------------------

export const advaInvoiceStatuses = [
  "draft",
  "open",
  "paid",
  "void",
  "uncollectible",
] as const;

export const AdvaInvoiceInputSchema = z.object({
  partner_external_id: z.string().describe("Partner External ID"),
  customer_partner_external_id: z
    .string()
    .optional()
    .describe("Customer Partner External ID"),
  invoice_number: z.string().optional().describe("Invoice Number"),
  amount_cents: z.number().int().optional().describe("Amount (cents)"),
  currency: z.string().length(3).optional().describe("Currency"),
  status: z.enum(advaInvoiceStatuses).optional().describe("Status"),
  issued_at: z.string().datetime().optional().describe("Issued At"),
  due_at: z.string().datetime().optional().describe("Due At"),
});
export type AdvaInvoiceInput = z.infer<typeof AdvaInvoiceInputSchema>;

// ---------------------------------------------------------------------------
// AdvaPaymentInput — minimum needed to upsert into finance.transactions
// ---------------------------------------------------------------------------

export const advaPaymentMethods = [
  "card",
  "ach",
  "check",
  "cash",
  "wire",
  "other",
] as const;

export const AdvaPaymentInputSchema = z.object({
  partner_external_id: z.string().describe("Partner External ID"),
  invoice_partner_external_id: z
    .string()
    .optional()
    .describe("Invoice Partner External ID"),
  amount_cents: z.number().int().describe("Amount (cents)"),
  currency: z.string().length(3).optional().describe("Currency"),
  method: z.enum(advaPaymentMethods).optional().describe("Payment Method"),
  paid_at: z.string().datetime().optional().describe("Paid At"),
});
export type AdvaPaymentInput = z.infer<typeof AdvaPaymentInputSchema>;

// ---------------------------------------------------------------------------
// AdvaOpportunityInput — minimum needed to upsert into sales.opportunities
// ---------------------------------------------------------------------------

export const AdvaOpportunityInputSchema = z.object({
  partner_external_id: z.string().describe("Partner External ID"),
  customer_partner_external_id: z
    .string()
    .optional()
    .describe("Customer Partner External ID"),
  name: z.string().optional().describe("Name"),
  stage: z.string().optional().describe("Stage"),
  value_cents: z.number().int().optional().describe("Value (cents)"),
  currency: z.string().length(3).optional().describe("Currency"),
  source: z.string().optional().describe("Source"),
  opened_at: z.string().datetime().optional().describe("Opened At"),
  closed_at: z.string().datetime().optional().describe("Closed At"),
});
export type AdvaOpportunityInput = z.infer<typeof AdvaOpportunityInputSchema>;

// ---------------------------------------------------------------------------
// AdvaMediaInput — minimum needed to upsert into media.media_items
// ---------------------------------------------------------------------------

export const advaMediaKinds = ["photo", "video", "document"] as const;

export const AdvaMediaInputSchema = z.object({
  partner_external_id: z.string().describe("Partner External ID"),
  subject_partner_external_id: z
    .string()
    .optional()
    .describe("Subject Partner External ID"),
  kind: z.enum(advaMediaKinds).describe("Kind"),
  url: z.string().url().describe("URL"),
  caption: z.string().optional().describe("Caption"),
  captured_at: z.string().datetime().optional().describe("Captured At"),
});
export type AdvaMediaInput = z.infer<typeof AdvaMediaInputSchema>;

// ---------------------------------------------------------------------------
// AdvaCommunicationInput — minimum needed to upsert into core.communications
// ---------------------------------------------------------------------------

export const advaCommunicationChannels = [
  "email",
  "sms",
  "call",
  "voicemail",
  "chat",
  "other",
] as const;

export const advaCommunicationDirections = ["inbound", "outbound"] as const;

export const AdvaCommunicationInputSchema = z.object({
  partner_external_id: z.string().describe("Partner External ID"),
  contact_partner_external_id: z
    .string()
    .optional()
    .describe("Contact Partner External ID"),
  channel: z.enum(advaCommunicationChannels).describe("Channel"),
  direction: z.enum(advaCommunicationDirections).describe("Direction"),
  subject: z.string().optional().describe("Subject"),
  body: z.string().optional().describe("Body"),
  occurred_at: z.string().datetime().optional().describe("Occurred At"),
});
export type AdvaCommunicationInput = z.infer<
  typeof AdvaCommunicationInputSchema
>;
